import { err, ok, type Result } from '../result.ts';

// Referrals (docs/plans/phase2-referrals.md §5). An outgoing referral is opened when its
// letter is issued and moves Sent → Acknowledged → Attended → Closed; an incoming one is
// logged at reception and moves Received → Feedback sent → Closed. Steps are recorded as
// updates with their own clinical time; nothing goes back, and entered in error is final.
// The steps and words are provisional (plan F2–F5, TODO(clinical-verify)).

export const REFERRAL_DIRECTIONS = ['outgoing', 'incoming'] as const;
export type ReferralDirection = (typeof REFERRAL_DIRECTIONS)[number];

export const REFERRAL_DIRECTION_LABELS: Record<ReferralDirection, string> = {
  outgoing: 'Sent',
  incoming: 'Received',
};

export const REFERRAL_STATUSES = [
  'sent',
  'acknowledged',
  'attended',
  'received',
  'feedback_sent',
  'closed',
  'entered_in_error',
] as const;
export type ReferralStatus = (typeof REFERRAL_STATUSES)[number];

/** Statuses each direction can be in. */
export const REFERRAL_STATUSES_BY_DIRECTION: Record<ReferralDirection, readonly ReferralStatus[]> =
  {
    outgoing: ['sent', 'acknowledged', 'attended', 'closed', 'entered_in_error'],
    incoming: ['received', 'feedback_sent', 'closed', 'entered_in_error'],
  };

export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  sent: 'Sent',
  acknowledged: 'Acknowledged',
  attended: 'Attended',
  received: 'Received',
  feedback_sent: 'Feedback sent',
  closed: 'Closed',
  entered_in_error: 'Entered in error',
};

export const REFERRAL_ORIGINS = ['consultation', 'ward_round', 'casualty', 'reception'] as const;
export type ReferralOrigin = (typeof REFERRAL_ORIGINS)[number];

export const REFERRAL_ORIGIN_LABELS: Record<ReferralOrigin, string> = {
  consultation: 'Consultation',
  ward_round: 'Ward round',
  casualty: 'Casualty',
  reception: 'Reception',
};

export const REFERRAL_EVENT_KINDS = [
  'sent',
  'received',
  'acknowledged',
  'attended',
  'feedback_received',
  'feedback_sent',
  'closed',
  'note',
  'entered_in_error',
] as const;
export type ReferralEventKind = (typeof REFERRAL_EVENT_KINDS)[number];

export const REFERRAL_EVENT_KIND_LABELS: Record<ReferralEventKind, string> = {
  sent: 'Referral letter issued',
  received: 'Referral received',
  acknowledged: 'Acknowledged by the receiving facility',
  attended: 'Patient attended',
  feedback_received: 'Feedback received',
  feedback_sent: 'Feedback letter issued',
  closed: 'Closed',
  note: 'Note',
  entered_in_error: 'Entered in error',
};

/** What a person records with "Record update"; the letters record their own steps. */
export const REFERRAL_UPDATE_KINDS = [
  'acknowledged',
  'attended',
  'feedback_received',
  'closed',
  'note',
] as const;
export type ReferralUpdateKind = (typeof REFERRAL_UPDATE_KINDS)[number];

export const REFERRAL_CHANNELS = ['phone', 'letter', 'in_person', 'other'] as const;
export type ReferralChannel = (typeof REFERRAL_CHANNELS)[number];

export const REFERRAL_CHANNEL_LABELS: Record<ReferralChannel, string> = {
  phone: 'Phone call',
  letter: 'Letter',
  in_person: 'In person',
  other: 'Other',
};

export const REFERRAL_HIE_SHARES = [
  'queued',
  'no_consent',
  'not_registry_facility',
  'not_applicable',
  'cancelled',
] as const;
export type ReferralHieShare = (typeof REFERRAL_HIE_SHARES)[number];

/** Where a referral was written, from the visit type (plan §2.1). */
export function referralOrigin(visitType: string): ReferralOrigin {
  if (visitType === 'inpatient') return 'ward_round';
  // The casualty slice's visit type; compared as text so this works before it merges.
  if (visitType === 'emergency') return 'casualty';
  return 'consultation';
}

export type ReferralUpdateErrorCode =
  | 'referral_update.not_allowed'
  | 'referral_update.entered_in_error'
  | 'referral_update.note_required'
  | 'referral_update.close_needs_note';

const NEXT_STEPS: Record<
  ReferralDirection,
  Partial<Record<ReferralStatus, readonly ReferralUpdateKind[]>>
> = {
  outgoing: {
    sent: ['acknowledged', 'attended', 'feedback_received', 'closed', 'note'],
    acknowledged: ['attended', 'feedback_received', 'closed', 'note'],
    attended: ['feedback_received', 'closed', 'note'],
    // Feedback can arrive after a referral was closed (F4).
    closed: ['feedback_received'],
  },
  incoming: {
    received: ['closed', 'note'],
    feedback_sent: ['closed', 'note'],
  },
};

/** The updates a person may record next, in the order the form offers them. */
export function allowedReferralUpdates(
  direction: ReferralDirection,
  status: ReferralStatus,
): readonly ReferralUpdateKind[] {
  return NEXT_STEPS[direction][status] ?? [];
}

const STATUS_AFTER: Partial<Record<ReferralUpdateKind, ReferralStatus>> = {
  acknowledged: 'acknowledged',
  attended: 'attended',
  closed: 'closed',
};

/**
 * The status after an update, or why it is refused. `feedback_sent` is recorded by issuing
 * a feedback letter on an incoming referral; it moves Received to Feedback sent and leaves a
 * later status as it is (a second letter corrects the first).
 */
export function referralUpdate(
  direction: ReferralDirection,
  status: ReferralStatus,
  kind: ReferralUpdateKind | 'feedback_sent',
  facts: { hasNote: boolean; wasAttended: boolean },
): Result<{ status: ReferralStatus }, ReferralUpdateErrorCode> {
  if (status === 'entered_in_error') return err('referral_update.entered_in_error');
  if (kind === 'feedback_sent') {
    if (direction !== 'incoming') return err('referral_update.not_allowed');
    return ok({ status: status === 'received' ? 'feedback_sent' : status });
  }
  if (!allowedReferralUpdates(direction, status).includes(kind)) {
    return err('referral_update.not_allowed');
  }
  if ((kind === 'note' || kind === 'feedback_received') && !facts.hasNote) {
    return err('referral_update.note_required');
  }
  // F3: closing a referral the patient never attended says why.
  if (
    kind === 'closed' &&
    direction === 'outgoing' &&
    !facts.wasAttended &&
    status !== 'attended' &&
    !facts.hasNote
  ) {
    return err('referral_update.close_needs_note');
  }
  return ok({ status: STATUS_AFTER[kind] ?? status });
}

/** A minute's grace for clocks a little ahead of the server's. */
const FUTURE_GRACE_MS = 60_000;

export type ReferralUpdateTimeProblem = 'future' | 'before_referral';

/** When an update happened: not in the future, not before the referral itself. */
export function referralUpdateTimeProblem(
  at: Date,
  context: { referredAt: Date; now: Date },
): ReferralUpdateTimeProblem | null {
  if (at.getTime() > context.now.getTime() + FUTURE_GRACE_MS) return 'future';
  if (at.getTime() < context.referredAt.getTime()) return 'before_referral';
  return null;
}

const PRINTED_LETTER = 'the printed letter is what the patient takes.';

/**
 * The national referral exchange state in words (plan §4): why nothing was shared, or where
 * the outbox event is. `outbox` is null when no event was written.
 */
export function referralHieShareWords(input: {
  share: ReferralHieShare;
  outbox: { status: 'pending' | 'retrying' | 'sent' | 'dead'; lastErrorCode: string | null } | null;
}): string {
  switch (input.share) {
    case 'no_consent':
      return 'Not shared: no data-sharing consent';
    case 'not_registry_facility':
      return 'Not shared: the facility was typed, not chosen from the national registry';
    case 'not_applicable':
      return 'Not shared: referred before sharing existed';
    case 'cancelled':
      return 'Not shared: entered in error before it was sent';
    case 'queued':
      break;
  }
  const outbox = input.outbox;
  if (outbox === null || outbox.status === 'pending') return 'Waiting to share';
  if (outbox.status === 'sent') return 'Shared with the national referral exchange';
  const code = outbox.lastErrorCode ?? 'unknown error';
  if (code === 'not_configured') {
    return `Couldn't share: the national referral exchange isn't set up yet. The printed letter is what the patient takes.`;
  }
  if (outbox.status === 'retrying') {
    return `Couldn't share yet (${code}). Wodi tries again; ${PRINTED_LETTER}`;
  }
  return `Couldn't share (${code}). The printed letter is what the patient takes.`;
}
